<template>
  <div class="tableWithPerPageSelection">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">{{ $t('listeRetrocession.title')}}</h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="level mgt-20px">
      <div class="level-left">
        <form @submit.prevent="recherche()"
              class="column is-narrow pdr-0">
          <b-input :placeholder="$t('general.button.search')"
                   type="search"
                   icon="magnify"
                   v-model="criteresRecherche.auto">
          </b-input>
        </form>
        <div class="column is-narrow">
          <button class="button" @click='setShowFiltre'>
            <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
            <span class="is-size-7">
                {{$t('general.button.filtre')}}
                <label class="has-text-weight-bold"
                       :class="nombreCritere > 0 ? '' : 'is-invisible'">
                  ({{ nombreCritere }})
                </label>
              </span>
          </button>
          <span @click="reinitialiserFiltre()"
                class="info-circle-right is-pulled-right is-cursor-pointer">
              <b-icon icon="close-circle" type="is-danger"></b-icon>
            </span>
        </div>
        <div class='column is-narrow'>
          <date-picker :dates='criteresRecherche.datesFacture'
                       :triggerSearchEveryTime="false"
                       :updateEndDateAtChangeStartDate="false"
                       @recherche="recherche"/>
        </div>
      </div>
      <div class="columns level-right">
        <div class="column is-narrow">
          <exportExcelButton
            :perm="constantes.RETROCESSION.exporter"
            :query="exportExcelQuery"/>
        </div>
        <parametrage-table
          :reference-table="constantes.REFERENCE_TABLE_RETROCESSION"
          @endLoadParamtre="startArrayRendering = true">
        </parametrage-table>
      </div>
    </div>
    <v-server-table
      url=""
      :columns="columns"
      :options="options"
      :ref="constantes.REFERENCE_TABLE_RETROCESSION"
      @loaded="finLoading" @loading="debutLoading"
      v-if="startArrayRendering && mountedLoadingEnd">
      <template slot='afterTable'>
        <table-items-in-view
          :perPage="resultPerPage"
          @input="$refs[constantes.REFERENCE_TABLE_RETROCESSION].setLimit($event)" />
      </template>
      <div slot="codeFacture" slot-scope="props">
        <router-link-blank
          :to="{ name: 'c.bodac.factures', params: { id: props.row.id }}"
          class="lien"
          :message="props.row.codeFacture">
        </router-link-blank>
      </div>
      <div slot="dateFacture" slot-scope="props">
        {{ props.row.dateFacture | formatDate }}
      </div>
      <div slot="montantHtFacture" slot-scope="props" class="has-text-right">
        {{ props.row.montantHtFacture | formatDevise }}
      </div>
      <div slot="montantTtcFacture" slot-scope="props" class="has-text-right">
        {{ props.row.montantTtcFacture | formatDevise }}
      </div>
      <div slot="totalHtLocation" slot-scope="props" class="has-text-right">
        {{ props.row.totalHtLocation | formatDevise }}
      </div>
      <div slot="retrocession" slot-scope="props" class="has-text-right">
        {{ props.row.retrocession | formatDevise }}
      </div>
      <div slot="pourcentageConcession" slot-scope="props" class="has-text-right">
        <div v-if="props.row.pourcentageConcession">
          {{ props.row.pourcentageConcession | formatPercent }}
        </div>
      </div>
      <div slot="pourcentageRetrocession" slot-scope="props" class="has-text-right">
        <div v-if="props.row.pourcentageRetrocession">
          {{ props.row.pourcentageRetrocession | formatPercent }}
        </div>
      </div>
      <div slot="annonceursNomsCommerciaux" slot-scope="props">
        {{ props.row.annonceursNomsCommerciaux.join(', ')}}
      </div>
      <div slot="etatFacture" slot-scope="props">
        {{ getEtatFacture(props.row.etatFacture) }}
      </div>
      <div slot="sousTypeFacture" slot-scope="props">
        {{ getSousTypeFacture(props.row.sousTypeFacture) }}
      </div>
      <div slot="agence" slot-scope="props">
        <div>{{ getAgenceLibelle(props.row.agenceId) }}</div>
      </div>
    </v-server-table>
    <transition name="custom-classes-transition"
                enter-active-class="animate__animated animate__slideInRight"
                leave-active-class="animate__animated animate__slideOutRight" :duration="10">
      <div class="componentBrief" v-show="showFiltre === true">
        <filtre-liste-retrocession @toggleFiltre="setShowFiltre" ref="filtreListeRetrocession"
                           @recherche="makeRechercheFilter()"/>
      </div>
    </transition>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import { RETROCESSION, FACTURES } from '@/configuration/permissionsBodacConf';
import breadcrumb from '@/components/general/breadcrumb';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import {
  FACTURE_ETAT,
  FACTURE_SOUS_TYPE,
} from '@/configuration/facture/factureConf';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import cloneDeep from 'lodash/cloneDeep';
import formatIsoDate from '@/services/general/formatIsoDate';
import messageErreur from '@/services/general/messageErreur';
import subscribeMutation from '@/services/store/subscribeMutation';
import TableItemsInView from '@/components/general/TableItemsInView';
import filtreListeRetrocession from '@/components/bodac/retrocession/filtreListeRetrocession';
import datePicker from '@/components/general/datePicker';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import EXPORT_EXCEL_TYPE_EXPORT_RETROCESSION
  from '@/configuration/bodac/retrocession/retrocessionConf';
import generalTransformer from '@/services/general/filters/serverTableDataTransformer';
import { columnsSplice } from '@/services/general/columns';
import { REFERENCE_TABLE_RETROCESSION } from '@/configuration/general/referenceTableConf';
import parametrageTable from '@/components/general/parametrageTable';
import exportExcelButton from '@/components/general/button/exportExcelButton';

const Retrocession = createNamespacedHelpers('retrocession');
const Societe = createNamespacedHelpers('societe');

export default {
  data() {
    return {
      unsubscribeValue: null,
      showFiltre: false,
      startArrayRendering: false,
      mountedLoadingEnd: false,
      constantes: {
        RETROCESSION,
        FACTURES,
        REFERENCE_TABLE_RETROCESSION,
      },
      resultPerPage: RESULTS_PER_PAGE,
    };
  },
  components: {
    appBreadcrumb: breadcrumb,
    TableItemsInView,
    filtreListeRetrocession,
    datePicker,
    routerLinkBlank,
    parametrageTable,
    exportExcelButton,
  },
  computed: {
    ...Retrocession.mapGetters({
      criteresRecherche: 'criteresRecherche',
      total: 'totalCount',
      lastPayload: 'lastPayload',
      items: 'items',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      agencesSelectionnees: 'agenceActive',
      societeActive: 'societeActive',
      societesInformations: 'societesInformations',
      lockMenu: 'lockMenu',
    }),
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('listeRetrocession.breadcrumb.liste'), route: { name: '' }, isActive: true },
      ];
    },
    nombreCritere() {
      const banCritereKey = ['auto', 'datesFacture'];
      const nombreCritere = calculNombreCritere(this.criteresRecherche, banCritereKey);
      if (nombreCritere > 9) return '9+';
      return nombreCritere;
    },
    columns() {
      const columns = ['codeFacture', 'dateFacture', 'sousTypeFacture', 'montantHtFacture', 'montantTtcFacture', 'annonceursNomsCommerciaux', 'etatFacture', 'libelleRegroupement', 'totalHtLocation', 'pourcentageConcession', 'nonCommercialTiers', 'pourcentageRetrocession', 'retrocession', 'agence'];
      if (this.societeActive.agences.length === 1) {
        columnsSplice(columns, 'agence');
      }
      return columns;
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          codeFacture: this.$i18n.t('listeRetrocession.tab.code'),
          dateFacture: this.$i18n.t('listeRetrocession.tab.date'),
          montantHtFacture: this.$i18n.t('listeRetrocession.tab.montantHt'),
          montantTtcFacture: this.$i18n.t('listeRetrocession.tab.montantTtc'),
          etatFacture: this.$i18n.t('listeRetrocession.tab.etat'),
          sousTypeFacture: this.$i18n.t('listeRetrocession.tab.sousType'),
          agence: this.$i18n.t('listeRetrocession.tab.agence'),
          annonceursNomsCommerciaux: this.$i18n.t('listeRetrocession.tab.annonceur'),
          libelleRegroupement: this.$i18n.t('listeRetrocession.tab.libelleRegroupement'),
          totalHtLocation: this.$i18n.t('listeRetrocession.tab.totalHtLoc'),
          nonCommercialTiers: this.$i18n.t('listeRetrocession.tab.bailleur'),
          pourcentageRetrocession: this.$i18n.t('listeRetrocession.tab.pourcentageRetrocession'),
          retrocession: this.$i18n.t('listeRetrocession.tab.retrocession'),
          pourcentageConcession: this.$i18n.t('listeRetrocession.tab.pourcentageConcessionnaire'),
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        requestFunction: ((data) => {
          data.query = cloneDeep(this.criteresRecherche);
          if (this.criteresRecherche.agencesId.length === 0) {
            data.query.agencesId = this.agencesSelectionnees
              .map((agence) => agence.agenceId);
          }
          if (data.query.datesFacture) {
            data.query.datesFacture.dateDebut = formatIsoDate(data.query.datesFacture.dateDebut);
            data.query.datesFacture.dateFin = formatIsoDate(data.query.datesFacture.dateFin);
          }
          if (data.query.annonceurs.length > 0) {
            data.query.annonceursId = data.query.annonceurs.map((client) => client.id);
            delete data.query.annonceurs;
          }
          if (data.query.bailleurs.length > 0) {
            data.query.tiersId = data.query.bailleurs.map((bailleur) => bailleur.id);
            delete data.query.bailleurs;
          }
          data.query.societeId = this.societeActiveId;
          return this.loadRetrocession(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.items,
            count: this.total,
          };
        }),
        columnsClasses: {},
        sortable: ['codeFacture', 'dateFacture', 'montantHtFacture', 'montantTtcFacture', 'annonceursNomsCommerciaux', 'etatFacture', 'sousTypeFacture', 'libelleRegroupement', 'totalHtLocation', 'pourcentageConcession', 'nonCommercialTiers', 'pourcentageRetrocession', 'retrocession', 'agence'],
      };
    },
    exportExcelQuery() {
      return {
        typeDonnee: EXPORT_EXCEL_TYPE_EXPORT_RETROCESSION,
        typeExport: '',
        filtres: generalTransformer(this.lastPayload),
      };
    },
  },
  methods: {
    ...Retrocession.mapActions({
      loadRetrocession: 'loadRetrocession',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    async reinitialiserFiltre() {
      await this.$refs.filtreListeRetrocession.initializeFiltre();
      this.$refs.filtreListeRetrocession.viderCritere();
      await this.$refs.filtreListeRetrocession.appliquerCritere();
      this.showFiltre = false;
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListeRetrocession.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    getAgenceLibelle(agenceId) {
      let libelleAgence = '';
      this.societesInformations.forEach((societe) => {
        societe.agences.forEach((agence) => {
          if (agence.id === agenceId) libelleAgence = agence.libelle;
        });
      });
      return libelleAgence;
    },
    getEtatFacture(etatFacture) {
      const etatFind = FACTURE_ETAT.find((etat) => {
        return etat.code === etatFacture;
      });
      return etatFind ? this.$i18n.t(etatFind.libelle) : '';
    },
    getSousTypeFacture(sousTypeFacture) {
      const sousTypeFind = FACTURE_SOUS_TYPE.find((sousType) => {
        return sousType.code === sousTypeFacture;
      });
      return sousTypeFind ? this.$i18n.t(sousTypeFind.libelle) : '';
    },
    updateAgenceAndRecherche() {
      this.criteresRecherche.agencesId = this.agencesSelectionnees.map((agence) => agence.agenceId);
      this.recherche();
    },
    async changeValueShowFiltreAndLockMenu() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListeRetrocession.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.changeValueShowFiltreAndLockMenu();
      this.recherche();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    recherche() {
      this.$refs[REFERENCE_TABLE_RETROCESSION].refresh();
    },
    goFirstPage() {
      this.$refs[REFERENCE_TABLE_RETROCESSION].setPage(1);
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.setInformationPage(this.$t('pageGescom.retrocession.liste'));
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.updateAgenceAndRecherche, this.goFirstPage);
      this.mountedLoadingEnd = true;
      loadingComponent.close();
    } catch (error) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw error;
    }
  },
};

</script>
